import cx from 'classnames';
import type { VoidFunctionComponent } from 'react';

import { useQuery } from '../../../contexts/RouterContext';
import { icons } from '../../generic/Icon';
import Loader from '../../generic/Loader';
import { LoadingBar } from '../../generic/LoadingBar/LoadingBar';
import TooltipIcon from '../../generic/TooltipIcon';
import type { ReportsV3QueryParams } from '../types';

import { useLoadPreview, usePreviewURL } from './hooks';

import styles from './Preview.module.less';

/**
 * Renders the loading state for the preview.
 */
const PreviewLoading: VoidFunctionComponent = () => (
  <div className={styles.loading}>
    <Loader />
    <span className={styles.loadingText}>
      Your sample report is being generated...
    </span>
  </div>
);

/**
 * Renders the preview for the report. Really it's just an iframe which hits the
 * preview endpoint (which is what actually serves the HTML content for the report preview).
 * URL changes are debounced by useLoadPreview before the iframe navigates.
 */
export const Preview: VoidFunctionComponent = () => {
  const [query] = useQuery<ReportsV3QueryParams>();
  const isPDF = query.format === 'PDF';
  const previewURL = usePreviewURL();
  const { cacheBustedURL, debouncedPreviewURL, isLoading, isPending, onIframeLoad } = useLoadPreview(previewURL);

  return (
    <div className={styles.preview}>
      <header className={styles.header}>
        <span className={styles.headerTitle}>
          Preview
        </span>
        <TooltipIcon
          id="report-preview-tooltip"
          icon={icons.circleQuestion}
          tooltip="Previews use sample data and only show a limited number of dependencies and licenses"
        />
      </header>
      <LoadingBar isVisible={isPending || isLoading} />
      {isLoading && <PreviewLoading />}
      <iframe
        key={cacheBustedURL}
        src={debouncedPreviewURL}
        title="report-preview"
        onLoad={onIframeLoad}
        // Note that we always render the iFrame (so that it loads), but we hide
        // it with display: none while the new content is loading. During the
        // pending (debounce) phase the old preview stays visible.
        className={cx(styles.iframe, isLoading && styles.iframeLoading)}
        // Chrome's built-in PDF viewer (PDFium) cannot render inside sandboxed
        // iframes regardless of which sandbox flags are set. Firefox's PDF.js
        // viewer is unaffected. We omit the sandbox attribute for PDF previews
        // so they display correctly in Chrome.
        sandbox={isPDF ? undefined : 'allow-same-origin allow-scripts'}
      />
    </div>
  );
};
